import useDialogConfirm from '@/components/dialogConfirm/hooks/useDialogConfirm';
import { useCallback, useEffect, useRef } from 'react';

interface UseSaveChangeBeforeLeaveThePageProps {
  onConfirmSaveChanges?: () => void;
  shouldConfirm?: boolean;
}

export const useSaveChangeBeforeLeaveThePage = ({
  onConfirmSaveChanges,
  shouldConfirm,
}: UseSaveChangeBeforeLeaveThePageProps) => {
  const controller = useRef<AbortController | null>(null);
  const answered = useRef(false);

  const {
    useOpen: openConfirmationDialog,
    useFnCallback,
    useConfirm: confirm,
  } = useDialogConfirm();

  const action = useCallback(
    async (event: Event, element?: Element) => {
      if (answered.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const saveChangeConfirmation = await confirm();
      answered.current = true;

      setTimeout(() => {
        answered.current = false;
      }, 500);

      if (saveChangeConfirmation === null) {
        return;
      }

      if (element && !saveChangeConfirmation) {
        if (element instanceof HTMLAnchorElement) {
          const clickEvent = new MouseEvent('click');
          element.dispatchEvent(clickEvent);
        } else {
          element.dispatchEvent(event);
        }

        return;
      }

      if (saveChangeConfirmation) {
        onConfirmSaveChanges?.();
      }
    },
    [confirm, onConfirmSaveChanges],
  );

  const detectLeavePage = useCallback((action: (event: MouseEvent, element?: Element) => void) => {
    const controller = new AbortController();

    const menuItems = document.querySelectorAll('aside a[class*="menuItem"]');
    menuItems?.forEach((menuItem) => {
      menuItem?.addEventListener(
        'click',
        (event) => {
          action(event as MouseEvent, menuItem);
        },
        { signal: controller.signal },
      );
    });

    const buttonLogout = document.querySelector('#logout button') as HTMLButtonElement;
    buttonLogout?.addEventListener(
      'click',
      (event) => {
        action(event, buttonLogout);
      },
      { signal: controller.signal },
    );

    return controller;
  }, []);

  useEffect(() => {
    controller.current?.abort();
    controller.current = detectLeavePage(action);

    if (!shouldConfirm) {
      controller.current?.abort();
    }
  }, [detectLeavePage, action, shouldConfirm]);

  return {
    openConfirmationDialog,
    answerConfirmation: useFnCallback,
  };
};
